import {
  BUDGET_CAPTURE_CROP_PADDING,
  BUDGET_CAPTURE_FRAME_MARGIN,
  BUDGET_CAPTURE_VIEW_ELEVATION_DEG,
  CAMERA_3D_FAR,
  CAMERA_3D_FOV,
  CAMERA_3D_NEAR,
} from '@constants'
import type { Item, LinkedItem, SceneObjects } from '@types'
import isItem from '@utils/sceneObjects/assertions/isItem'
import isLinkedItem from '@utils/sceneObjects/assertions/isLinkedItem'
import { buildItemOBBFromMatrix } from '@utils/sceneObjects/items/itemObbUtils'
import { Box3, MathUtils, Matrix4, PerspectiveCamera, Quaternion, Vector3 } from 'three'

export type BudgetCapturePose = {
  position: Vector3
  target: Vector3
  up: Vector3
  fov: number
  near: number
  far: number
}

export type BudgetCaptureCropRect = {
  x: number
  y: number
  width: number
  height: number
}

const _corner = new Vector3()
const _offset = new Vector3()
const _viewDir = new Vector3()
const _forward = new Vector3()
const _right = new Vector3()
const _up = new Vector3()
const _cameraUp = new Vector3()
const _localRight = new Vector3()
const _rightOnGround = new Vector3()
const _center = new Vector3()
const _size = new Vector3()
const _position = new Vector3()
const _projected = new Vector3()
const _anchorPos = new Vector3()
const _anchorQuat = new Quaternion()
const _anchorScale = new Vector3()

const OBB_CORNER_SIGNS: Array<[number, number, number]> = [
  [-1, -1, -1],
  [-1, -1, 1],
  [-1, 1, -1],
  [-1, 1, 1],
  [1, -1, -1],
  [1, -1, 1],
  [1, 1, -1],
  [1, 1, 1],
]

function getBoxCorners(box: Box3): Array<[number, number, number]> {
  return [
    [box.min.x, box.min.y, box.min.z],
    [box.min.x, box.min.y, box.max.z],
    [box.min.x, box.max.y, box.min.z],
    [box.min.x, box.max.y, box.max.z],
    [box.max.x, box.min.y, box.min.z],
    [box.max.x, box.min.y, box.max.z],
    [box.max.x, box.max.y, box.min.z],
    [box.max.x, box.max.y, box.max.z],
  ]
}

/**
 * AABB world-space de todas las unidades, usando las 8 esquinas del OBB
 * para respetar rotación (cualquier distribución).
 */
export function computeItemsWorldBoundingBox(
  items: Map<string, SceneObjects | Item | LinkedItem>,
): Box3 | null {
  const box = new Box3()
  let hasContent = false

  for (const item of items.values()) {
    if (!(isItem(item) || isLinkedItem(item))) continue
    if (!item.matrix || !item.width || !item.height) continue
    if (item.visible === false) continue

    const thickness = item.thickness || 0
    const obb = buildItemOBBFromMatrix(item.matrix as Matrix4, item.width, item.height, thickness)

    const hx = obb.halfSize.x
    const hy = obb.halfSize.y
    const hz = obb.halfSize.z

    for (const [sx, sy, sz] of OBB_CORNER_SIGNS) {
      _corner
        .set(sx * hx, sy * hy, sz * hz)
        .applyMatrix3(obb.rotation)
        .add(obb.center)
      if (!hasContent) {
        box.min.copy(_corner)
        box.max.copy(_corner)
        hasContent = true
      } else {
        box.expandByPoint(_corner)
      }
    }
  }

  if (!hasContent || box.isEmpty()) return null
  return box
}

/**
 * `up` cenital alineado al módulo ancla (mismo criterio que draw fit):
 * local +X → derecha de pantalla, sin rotar la unidad.
 */
export function resolveBudgetCaptureTopDownUp(
  items: Map<string, SceneObjects | Item | LinkedItem>,
): Vector3 {
  for (const item of items.values()) {
    if (!(isItem(item) || isLinkedItem(item))) continue
    if (!item.matrix || !item.width || !item.height) continue
    if (item.visible === false) continue

    item.matrix.decompose(_anchorPos, _anchorQuat, _anchorScale)
    _localRight.set(1, 0, 0).applyQuaternion(_anchorQuat)
    _rightOnGround.set(_localRight.x, 0, _localRight.z)

    if (_rightOnGround.lengthSq() < 1e-6) {
      return new Vector3(0, 0, 1)
    }

    _rightOnGround.normalize()
    return new Vector3(_rightOnGround.z, 0, -_rightOnGround.x).normalize()
  }

  return new Vector3(0, 0, 1)
}

/** Vista desde arriba: elevación 90° (o la constante de captura). */
function setTopDownViewDirection(out: Vector3): Vector3 {
  const elevation = MathUtils.degToRad(BUDGET_CAPTURE_VIEW_ELEVATION_DEG)
  // Azimut irrelevante en 90°; mantenemos (0,1,0) exacto cuando es cenital.
  if (Math.abs(elevation - Math.PI / 2) < 1e-4) {
    return out.set(0, 1, 0)
  }
  const cosEl = Math.cos(elevation)
  return out.set(0, Math.sin(elevation), cosEl).normalize()
}

function setCameraBasis(viewDir: Vector3, cameraUp: Vector3): void {
  _forward.copy(viewDir).negate()
  _right.crossVectors(_forward, cameraUp)
  if (_right.lengthSq() < 1e-8) {
    _right.set(1, 0, 0)
  } else {
    _right.normalize()
  }
  _up.crossVectors(_right, _forward).normalize()
}

/**
 * Distancia mínima exacta para que el AABB quepa en el frustum.
 * Sin distancias fijas: escala con el tamaño real de la escena.
 */
export function distanceToFitBox(
  box: Box3,
  center: Vector3,
  viewDir: Vector3,
  cameraUp: Vector3,
  fovDeg: number,
  aspect: number,
  margin: number,
  near: number,
): number {
  setCameraBasis(viewDir, cameraUp)

  const vHalf = MathUtils.degToRad(fovDeg) / 2
  const hHalf = Math.atan(Math.tan(vHalf) * Math.max(aspect, 1e-6))
  const tanV = Math.tan(vHalf)
  const tanH = Math.tan(hHalf)

  let minDistance = near + 1

  for (const [cx, cy, cz] of getBoxCorners(box)) {
    _offset.set(cx, cy, cz).sub(center)
    const x = Math.abs(_offset.dot(_right))
    const y = Math.abs(_offset.dot(_up))
    const zOff = _offset.dot(_forward)

    minDistance = Math.max(minDistance, x / tanH - zOff, y / tanV - zOff)
  }

  return Math.max(minDistance, near + 1) * margin
}

/**
 * Pose de captura cenital: cámara dedicada sobre el AABB, orientada al módulo.
 */
export function computeBudgetCapturePose(
  box: Box3,
  items: Map<string, SceneObjects | Item | LinkedItem>,
  options: {
    aspect: number
    fov?: number
    near?: number
    far?: number
    margin?: number
  },
): BudgetCapturePose {
  const fov = options.fov ?? CAMERA_3D_FOV
  const near = options.near ?? CAMERA_3D_NEAR
  const far = options.far ?? CAMERA_3D_FAR
  const margin = options.margin ?? BUDGET_CAPTURE_FRAME_MARGIN
  const aspect = Math.max(options.aspect, 1e-6)

  box.getCenter(_center)
  box.getSize(_size)
  // Mirar al centro geométrico (vista cenital); ligero lift evita clipping del techo del AABB.
  _center.y = box.min.y + _size.y * 0.5

  _cameraUp.copy(resolveBudgetCaptureTopDownUp(items))
  setTopDownViewDirection(_viewDir)

  const distance = distanceToFitBox(box, _center, _viewDir, _cameraUp, fov, aspect, margin, near)
  _position.copy(_center).addScaledVector(_viewDir, distance)

  return {
    position: _position.clone(),
    target: _center.clone(),
    up: _cameraUp.clone(),
    fov,
    near,
    far,
  }
}

export function computeBudgetCaptureCropRect(
  box: Box3,
  camera: PerspectiveCamera,
  canvasWidth: number,
  canvasHeight: number,
  paddingRatio = 0.06,
): BudgetCaptureCropRect | null {
  if (canvasWidth < 2 || canvasHeight < 2) return null

  let minX = Number.POSITIVE_INFINITY
  let minY = Number.POSITIVE_INFINITY
  let maxX = Number.NEGATIVE_INFINITY
  let maxY = Number.NEGATIVE_INFINITY

  for (const [cx, cy, cz] of getBoxCorners(box)) {
    _projected.set(cx, cy, cz).project(camera)
    if (!Number.isFinite(_projected.x) || !Number.isFinite(_projected.y)) continue

    const sx = (_projected.x * 0.5 + 0.5) * canvasWidth
    const sy = (-_projected.y * 0.5 + 0.5) * canvasHeight
    minX = Math.min(minX, sx)
    maxX = Math.max(maxX, sx)
    minY = Math.min(minY, sy)
    maxY = Math.max(maxY, sy)
  }

  if (!Number.isFinite(minX) || !Number.isFinite(maxX) || maxX <= minX || maxY <= minY) {
    return null
  }

  return clampCropRect(minX, minY, maxX, maxY, canvasWidth, canvasHeight, paddingRatio)
}

/**
 * Recorte por píxeles de contenido (frente al fondo). Corrige el desfase
 * visual de composiciones en U/L donde el AABB geométrico no coincide
 * con la masa visual del sofá.
 */
export function computeContentAwareCropRect(
  source: HTMLCanvasElement,
  paddingRatio = BUDGET_CAPTURE_CROP_PADDING,
  tolerance = 14,
  sampleStep = 4,
): BudgetCaptureCropRect | null {
  const width = source.width
  const height = source.height
  if (width < 8 || height < 8) return null

  const tmp = document.createElement('canvas')
  tmp.width = width
  tmp.height = height
  const ctx = tmp.getContext('2d', { willReadFrequently: true })
  if (!ctx) return null

  try {
    ctx.drawImage(source, 0, 0)
  } catch {
    return null
  }

  const { data } = ctx.getImageData(0, 0, width, height)

  const sample = (x: number, y: number) => {
    const i = (y * width + x) * 4
    return [data[i], data[i + 1], data[i + 2]] as const
  }

  // Fondo = media de las 4 esquinas
  const corners = [
    sample(2, 2),
    sample(width - 3, 2),
    sample(2, height - 3),
    sample(width - 3, height - 3),
  ]
  const bgR = (corners[0][0] + corners[1][0] + corners[2][0] + corners[3][0]) / 4
  const bgG = (corners[0][1] + corners[1][1] + corners[2][1] + corners[3][1]) / 4
  const bgB = (corners[0][2] + corners[1][2] + corners[2][2] + corners[3][2]) / 4

  let minX = width
  let minY = height
  let maxX = 0
  let maxY = 0
  let found = false

  for (let y = 0; y < height; y += sampleStep) {
    for (let x = 0; x < width; x += sampleStep) {
      const i = (y * width + x) * 4
      const dr = Math.abs(data[i] - bgR)
      const dg = Math.abs(data[i + 1] - bgG)
      const db = Math.abs(data[i + 2] - bgB)
      if (dr > tolerance || dg > tolerance || db > tolerance) {
        found = true
        if (x < minX) minX = x
        if (y < minY) minY = y
        if (x > maxX) maxX = x
        if (y > maxY) maxY = y
      }
    }
  }

  if (!found || maxX <= minX || maxY <= minY) return null

  // Expandir un poco por el sampleStep
  minX = Math.max(0, minX - sampleStep)
  minY = Math.max(0, minY - sampleStep)
  maxX = Math.min(width - 1, maxX + sampleStep)
  maxY = Math.min(height - 1, maxY + sampleStep)

  return clampCropRect(minX, minY, maxX, maxY, width, height, paddingRatio)
}

function clampCropRect(
  minX: number,
  minY: number,
  maxX: number,
  maxY: number,
  canvasWidth: number,
  canvasHeight: number,
  paddingRatio: number,
): BudgetCaptureCropRect | null {
  const padX = (maxX - minX) * paddingRatio
  const padY = (maxY - minY) * paddingRatio

  const idealW = Math.min(canvasWidth, Math.ceil(maxX - minX + padX * 2))
  const idealH = Math.min(canvasHeight, Math.ceil(maxY - minY + padY * 2))
  const centerX = (minX + maxX) / 2
  const centerY = (minY + maxY) / 2

  let x = Math.round(centerX - idealW / 2)
  let y = Math.round(centerY - idealH / 2)
  x = Math.max(0, Math.min(x, canvasWidth - idealW))
  y = Math.max(0, Math.min(y, canvasHeight - idealH))

  if (idealW < 8 || idealH < 8) return null
  return { x, y, width: idealW, height: idealH }
}
